import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { TenancyMode, TenancyModeConfig } from 'src/types/tenant';

/**
 * Get the tenancy mode from runtime configuration
 * Defaults to 'multi' for backward compatibility
 */
export function getTenancyMode(): TenancyMode {
  const config = getRuntimeConfig();
  const mode = config.VITE_TENANCY_MODE;

  if (mode === 'single' || mode === 'multi') {
    return mode;
  }

  // Default to multi-tenant mode for backward compatibility
  return 'multi';
}

/**
 * Get the complete tenancy mode configuration
 */
export function getTenancyModeConfig(): TenancyModeConfig {
  const mode = getTenancyMode();

  switch (mode) {
    case 'single':
      return {
        mode: 'single',
        allowTenantCreation: true, // Allow creation of the first tenant
        allowTenantSwitching: false, // No switching in single-tenant mode
        maxTenants: 1,
      };
    case 'multi':
      return {
        mode: 'multi',
        allowTenantCreation: true,
        allowTenantSwitching: true,
        maxTenants: null, // Unlimited
      };
    default:
      return {
        mode: 'single',
        allowTenantCreation: false,
        allowTenantSwitching: false,
        maxTenants: 1,
      };
  }
}

/**
 * Check if tenant creation is allowed based on current mode and tenant count
 * In single-tenant mode, excludes system tenants from the count
 */
export function canCreateTenant(existingTenantCount: number): boolean {
  const config = getTenancyModeConfig();

  if (!config.allowTenantCreation) {
    return false;
  }

  if (config.maxTenants === null) {
    return true; // Unlimited tenants
  }

  return existingTenantCount < config.maxTenants;
}

/**
 * Check if organization tenant creation is allowed in single-tenant mode
 * This excludes system tenants from the count
 */
export function canCreateOrganizationTenant(
  allTenants: Array<{ name: string; schema: string }>,
): boolean {
  const config = getTenancyModeConfig();

  if (!config.allowTenantCreation) {
    return false;
  }

  if (config.mode !== 'single') {
    return canCreateTenant(allTenants.length);
  }

  // In single-tenant mode, count only non-system tenants
  const organizationTenants = allTenants.filter(
    (tenant) => !isSystemTenant(tenant),
  );

  return organizationTenants.length < (config.maxTenants ?? 1);
}

/**
 * Check if tenant switching is allowed
 */
export function canSwitchTenants(): boolean {
  const config = getTenancyModeConfig();
  return config.allowTenantSwitching;
}

/**
 * Reserved tenant names that cannot be used for organization tenants
 */
export const RESERVED_TENANT_NAMES = [
  'master',
  'system',
  'admin',
  'default',
  'root',
];

/**
 * Reserved schema patterns that indicate system tenants
 */
export const SYSTEM_SCHEMA_PATTERNS = ['master', 'system', 'admin', 'default'];

/**
 * Check if a tenant name is reserved for system use
 */
export function isReservedTenantName(name: string): boolean {
  return RESERVED_TENANT_NAMES.includes(name.toLowerCase());
}

/**
 * Check if a tenant is a system tenant based on its schema or name
 */
export function isSystemTenant(tenant: {
  name: string;
  schema: string;
}): boolean {
  // Defensive programming: check for null/undefined tenant or missing properties
  if (
    !tenant ||
    typeof tenant.name !== 'string' ||
    typeof tenant.schema !== 'string'
  ) {
    console.warn('isSystemTenant: Invalid tenant object:', tenant);
    return false; // Default to non-system tenant if data is invalid
  }

  const lowerName = tenant.name.toLowerCase();
  const lowerSchema = tenant.schema.toLowerCase();

  // Check if name is reserved
  if (RESERVED_TENANT_NAMES.includes(lowerName)) {
    return true;
  }

  // Check if schema matches system patterns
  return SYSTEM_SCHEMA_PATTERNS.some(
    (pattern) => lowerSchema.includes(pattern) || lowerSchema === pattern,
  );
}

/**
 * Get validation error message for tenant name
 */
export function validateTenantName(name: string): string | null {
  if (!name.trim()) {
    return 'Tenant name is required';
  }

  if (name.trim().length < 2) {
    return 'Tenant name must be at least 2 characters long';
  }

  if (isReservedTenantName(name.trim())) {
    return `"${name.trim()}" is a reserved system name. Please choose a different name for your organization.`;
  }

  // Check for invalid characters
  if (!/^[a-zA-Z0-9\s\-_]+$/.test(name.trim())) {
    return 'Tenant name can only contain letters, numbers, spaces, hyphens, and underscores';
  }

  return null;
}

/**
 * Get user-friendly display name for tenancy mode
 */
export function getTenancyModeDisplayName(mode: TenancyMode): string {
  switch (mode) {
    case 'single':
      return 'Single-Tenant Mode';
    case 'multi':
      return 'Multi-Tenant Mode';
    default:
      return 'Unknown Mode';
  }
}

/**
 * Get description for tenancy mode
 */
export function getTenancyModeDescription(mode: TenancyMode): string {
  switch (mode) {
    case 'single':
      return 'This deployment supports exactly one tenant. Tenant creation is limited to one tenant per deployment.';
    case 'multi':
      return 'This deployment supports multiple tenants. You can create and switch between multiple tenants.';
    default:
      return 'Unknown tenancy mode configuration.';
  }
}
